import { Component, HostListener, OnInit, AfterViewInit, ViewChild, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { iFrameUrlFactory } from '@utilities/iframe-url-factory';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { DocViewerParamReader } from '@utilities/doc-viewer-param-reader';
import { TabStates } from '@utilities/tab-states';
import { UserConfigsService } from 'src/app/services/user-configs.service';
import { Role } from 'src/app/models/role.model';
import { HelpService } from 'src/app/services/help.service';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Location } from '@angular/common';
import { isEqual } from 'lodash-es';
import { KeyboardShortcut, Shortcut } from "@models/keyboard-shortcut.model";
import { BaseComponent } from "../base.component";
import { MenuResizer } from "../review/browse/menu-resizer";
import { MainTabIndices } from 'src/app/enums/main-tab-indices.enum';
import { KeyboardShortcutSdk } from 'src/app/sdks/keyboard-shortcut.sdk';
import { InsightsUtility } from 'src/app/utilities/insights/insights.utility';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { ReviewManager } from 'src/app/managers/review.manager';
import { BrowserDataUtility } from 'src/app/utilities/browser-data.utility';
import { NotificationManager } from 'src/app/managers/notification.manager';
import { KeyboardShortcutAction } from "../../enums/keyboard-shortcut-action.enum";
import { DocCodingResponsiveness } from "../../enums/doc-coding-responsiveness.enum";
import { DocViewerTab } from "../../enums/doc-viewer-tab.enum";
import { KeyboardShortcutEvent } from 'src/app/insights/events/keyboard-shortcut.event';
import { Store } from "@ngrx/store";
import { documentViewActions } from '@store/doc-viewer/document-view/document-view.actions';
import { DocViewerActions } from "@store/doc-viewer/doc-viewer.actions";
import { PanelActions } from "@store/doc-viewer/panel/panel.actions";
import { LexbeThemeService } from "@services/lexbe-theme.service";
import { actionPanelActions } from '@store/doc-viewer/action-panel/action-panel.actions';
import { TabSyncService } from '@services/tab-sync.service';


@Component({
    selector: 'lex-doc-viewer',
    templateUrl: './doc-viewer.component.html',
    styleUrls: ['./doc-viewer.component.scss'],
    standalone: false
})
export class DocViewerComponent extends BaseComponent implements OnInit, AfterViewInit {
	public MainTabIndices = MainTabIndices;
	public mainTabVisibility$: Observable<any>;
	public isFirstUrlBuild = false;
	private reviewMode: boolean = false;
	private keyboardShortcuts: KeyboardShortcut[];
	public menuResizer: MenuResizer;
	public maxLeftPanelWidth: number = 500;
	public minLeftPanelWidth: number = 200;
	public leftPanelExpandWidth: number = 310;
	public leftPanelCollapseWidth: number = 55;

	@ViewChild(MatTabGroup) tabGroup: MatTabGroup;

	constructor(
		public iFrameUrlFactory: iFrameUrlFactory,
		public userConfigsService: UserConfigsService,
		public paramReader: DocViewerParamReader,
		public tabStates: TabStates,
		public reviewManager: ReviewManager,
		private docViewerStore: DocViewerStore,
		private router: Router,
		private location: Location,
		private route: ActivatedRoute,
		private helpService: HelpService,
		private keyboardShortcutSdk: KeyboardShortcutSdk,
		private browserDataUtility: BrowserDataUtility,
		private insightsUtility: InsightsUtility,
		private notificationManager: NotificationManager,
		private store: Store,
		private renderer: Renderer2,
		private themeService: LexbeThemeService,
		private tabSyncService: TabSyncService) {
		super();
		this.store.dispatch(DocViewerActions.loaded());
		this.store.dispatch(PanelActions.loadPanelStates());

		this.setTheme(this.themeService.isDarkMode());

		const leftPanelWidth = this.getLeftPanelWidth();
		this.menuResizer = new MenuResizer({ menuWidth: leftPanelWidth });
		this.helpService.updateHelpLink(this.userConfigsService.inRoles(Role.LimitedFeatureSet) ? 'limited-user' : 'document-viewer');
		this.paramReader.read(route.snapshot.queryParams);

		this.docViewerStore.updateState((currentState) => {
			currentState.reviewSetId = this.paramReader.reviewSetId;
			currentState.mode = currentState.reviewSetId ? 'review' : 'viewer';
			return currentState;
		});

		if (this.paramReader.fromSearch) {
			this.store.dispatch(actionPanelActions.viewUpdated({ selectedView: 'hits' }))
		}

		this.reviewManager.loadDocument(this.paramReader.docId);
		this.reviewManager.hasUnsavedChanges().subscribe((hasUnsavedChanges) => {
			if (hasUnsavedChanges)
				this.reviewManager.refreshDocument(true)
		});

		window.onmessage = ((ev: MessageEvent) => this.handleMessage(ev));

		this.docViewerStore.currentDocument$.pipe(
			takeUntil(this.destroy$)
		).subscribe({
			next: (document) => {
				this.onMainTabChange({ index: this.tabStates.mainTabSelectedIndex } as any);

				const newParams = this.paramReader.changeDocTo(document.docId);
				this.router.navigate([], {
					relativeTo: this.route,
					queryParams: newParams,
					queryParamsHandling: 'merge'
				}).toString();

				this.docViewerStore.updateState((currentState) => {
					currentState.redactionEditorPageNumber = 1;
					currentState.annotationEditorPageNumber = 1;
					if (newParams.DiffDocId && !this.isFirstUrlBuild) {
						currentState.compareDocumentId = newParams.DiffDocId;
						this.store.dispatch(documentViewActions.viewUpdated({ selectedView: 'text-compare' }));
					}
					this.isFirstUrlBuild = true;

					return currentState;
				});

				this.store.dispatch(DocViewerActions.pdfPageUpdate({ selectedPage: 1 }));
			}
		});

		this.keyboardShortcutSdk.get(browserDataUtility.getBrowserData().operatingSystem.toLowerCase() === 'macos').subscribe((data) => this.keyboardShortcuts = data);
	}

	@HostListener('window:beforeunload')
	public canDeactivate(): boolean {
		let canDeactivate = true;
		this.reviewManager.hasUnsavedChanges().subscribe((unsavedChanges) => {
			canDeactivate = !unsavedChanges
		})
		return canDeactivate;
	}

	@HostListener('document:keydown', ['$event'])
	public keyEvent(event: KeyboardEvent) {
		if (event.key === 'Alt' || event.key === 'Control' || event.key === 'Shift' || event.key === 'Meta')
			return;

		if (document.querySelector('[role="dialog"]'))
			return;

		if (document.activeElement?.classList?.contains('mat-mdc-input-element'))
			return;

		const shortcut = {
			altKey: event.altKey,
			ctrlKey: event.ctrlKey,
			shiftKey: event.shiftKey,
			metaKey: event.metaKey,
			code: event.code
		} as Shortcut;

		if (this.keyboardShortcuts.findIndex(x => isEqual(x.shortcutKey, shortcut)) === -1)
			return;

		const shortcutAction = this.keyboardShortcuts.find(x => isEqual(x.shortcutKey, shortcut)).shortcutAction;
		let performedAction = false;
		event.preventDefault();

		switch (shortcutAction) {
			case KeyboardShortcutAction.Save:
				if (!this.docViewerStore.documentCodingFormGroup?.errors?.redactForPrivilegeInvalid) {
					if (this.userConfigsService.inRoles(this.roles.DocReviewFeatureSet, this.roles.EditFeatureSet)) {
						this.notificationManager.displaySnackBarMessage({
							message: 'Saving Changes',
							ttl: 5000
						});
						this.reviewManager.requestSave();

						performedAction = true;
					}
				}
				break;

			case KeyboardShortcutAction.CodeResponsive:
			case KeyboardShortcutAction.CodeNonResponsive:
				if (this.userConfigsService.inRoles(this.roles.StandardFeatureSet, this.roles.DocReviewFeatureSet)) {
					this.docViewerStore.updateState((current) => {
						current.reviewedByMe = true;
						return current;
					}).subscribe();
					if (shortcutAction === KeyboardShortcutAction.CodeResponsive) {
						this.docViewerStore.documentCodingFormGroup.get('responsive').setValue(DocCodingResponsiveness.Responsive);
						this.notificationManager.displaySnackBarMessage({
							message: 'Marking document as Responsive',
							ttl: 5000
						});
					}
					else if (shortcutAction === KeyboardShortcutAction.CodeNonResponsive) {
						this.docViewerStore.documentCodingFormGroup.get('responsive').setValue(DocCodingResponsiveness.NonResponsive);
						this.notificationManager.displaySnackBarMessage({
							message: 'Marking document as Non-Responsive',
							ttl: 5000
						});
					}
					this.docViewerStore.documentCodingFormGroup.get('responsive').markAsDirty();
					this.docViewerStore.documentCodingFormGroup.markAsDirty();
					performedAction = true;
				}
				break;

			case KeyboardShortcutAction.PreviousDocument:
				this.reviewManager.goToPreviousDocument().subscribe((loadedDoc) => {
					if (loadedDoc) {
						performedAction = true;
						if (this.reviewMode)
							this.reviewManager.contextPanelScrollIntoView();
					}
				});
				break;

			case KeyboardShortcutAction.NextDocument:
				this.reviewManager.goToNextDocument().subscribe((loadedDoc) => {
					if (loadedDoc) {
						performedAction = true;
						if (this.reviewMode)
							this.reviewManager.contextPanelScrollIntoView();
					}
				});
				break;

			case KeyboardShortcutAction.DocumentAbove:
				if (this.reviewMode) {
					this.reviewManager.goToDocumentAbove().subscribe((loadedDoc) => {
						if (loadedDoc) {
							performedAction = true;
							this.reviewManager.contextPanelScrollIntoView();
						}
					});
				}
				break;

			case KeyboardShortcutAction.DocumentBelow:
				if (this.reviewMode) {
					this.reviewManager.goToDocumentBelow().subscribe((loadedDoc) => {
						if (loadedDoc) {
							performedAction = true;
							this.reviewManager.contextPanelScrollIntoView();
						}
					});
				}
				break;

			case KeyboardShortcutAction.DocumentAboveSkipAttachment:
				if (this.reviewMode) {
					this.reviewManager.goToDocumentAbove(true).subscribe((loadedDoc) => {
						if (loadedDoc) {
							performedAction = true;
							this.reviewManager.contextPanelScrollIntoView();
						}
					});
				}
				break;

			case KeyboardShortcutAction.DocumentBelowSkipAttachment:
				if (this.reviewMode) {
					this.reviewManager.goToDocumentBelow(true).subscribe((loadedDoc) => {
						if (loadedDoc) {
							performedAction = true;
							this.reviewManager.contextPanelScrollIntoView();
						}
					});
				}
				break;

			case KeyboardShortcutAction.Exit:
				if (this.reviewMode)
					this.reviewManager.exitReview();
				else
					window.close();
				performedAction = true;
				break;
		}

		if (performedAction) {
			this.insightsUtility.event(new KeyboardShortcutEvent(shortcutAction));
		}
	}

	public navigateToPage(pageNumber: number) {
		this.docViewerStore.updateState((updateSnapshot) => {
			if (pageNumber < 1) {
				pageNumber = 1;
			}
			if (pageNumber > updateSnapshot.maxPage) {
				pageNumber = updateSnapshot.maxPage;
			}
			updateSnapshot.currentPage = pageNumber;
			this.tabStates.changeMainTab(DocViewerTab.Page); // Observe store
			this.onMainTabChange({ index: this.tabStates.mainTabSelectedIndex } as any);
			return updateSnapshot;
		});

	}

	public onMainTabChange(event: MatTabChangeEvent): void {
		if (event.index === null || typeof event.index === 'undefined') {
			return;
		}
		this.reviewManager.changeCurrentTab(event.index);
	}

	public handleMessage(ev: MessageEvent) {
		const data = ev?.data?.split?.(':');
		if (data?.[0] === 'ViewPage') this.navigateToPage(Number(data[1]));
	}

	public ngOnInit(): void {
		this.mainTabVisibility$ = this.tabStates.visible.pipe(
			map((v: Array<Record<string, boolean>>) => {
				return {
					'hide-main-tab-1': !v[DocViewerTab.Hits],
					'hide-main-tab-2': !v[DocViewerTab.Original],
					'hide-main-tab-3': !v[DocViewerTab.Html],
					'hide-main-tab-4': !v[DocViewerTab.Page],
					'hide-main-tab-5': !v[DocViewerTab.Pdf],
					'hide-main-tab-6': !v[DocViewerTab.Text],
					'hide-main-tab-7': !v[DocViewerTab.Production],
					'hide-main-tab-8': !v[DocViewerTab.Translated],
					'hide-main-tab-9': !v[DocViewerTab.RedactedPdf],
					'hide-main-tab-10': !v[DocViewerTab.AnnotatedPdf],
					'hide-main-tab-11': !v[DocViewerTab.Compare]
				};
			})
		);
	}

	public ngAfterViewInit() {
		this.themeService.isDarkMode$.subscribe((value) => {
			this.setTheme(value);
		});
	}

	public setTheme(isDarkTheme: boolean) {
		if (isDarkTheme) {
			this.renderer.addClass(document.body, 'dark-theme');
		}
		else {
			this.renderer.removeClass(document.body, 'dark-theme');
		}
	}

	public onMenuResize(event: any) {
		this.docViewerStore.updateState((currentState) => {
			currentState.isContextPanelResizing = true;
			return currentState;
		});
		this.menuResizer.onMenuResize(event);
	}

	public getLeftPanelWidth(): number {
		const data = localStorage.getItem("docViewerContextPanel");
		const json = JSON.parse(data);
		return json?.width ? json.width : this.leftPanelExpandWidth;
	}

	public getLeftPanelState(panelState) {
		if (panelState == "expanded") {
			this.menuResizer.menuWidth = this.leftPanelExpandWidth;
		}
		if (panelState == "collapsed") {
			this.menuResizer.menuWidth = this.leftPanelCollapseWidth;
		}
	}

	@HostListener('window:mousemove', ['$event'])
	windowMouseMove(event: MouseEvent) {
		this.menuResizer.mouseMoveMinMax(event, this.minLeftPanelWidth, this.maxLeftPanelWidth);
	}

	@HostListener('window:mouseup')
	windowMouseUp() {
		this.docViewerStore.updateState((currentState) => {
			currentState.isContextPanelResizing = false;
			return currentState;
		});
		this.menuResizer.mouseUp();
	}
}
